<template>
  <div class="p-drawer reservation-sidebar">
    <!-- アイコン部 -->
    <div class="p-drawer__icons">
      <a
        :class="{ 'p-drawer__icons__expand': true, 'is-disable': isSidebarWidthMax }"
        @click="expand"
      >
        <v-icon
          icon="mdi-play-circle"
          size="large"
        />
      </a>
      <a
        :class="{ 'p-drawer__icons__shrink': true, 'is-disable': isSidebarWidthMin }"
        @click="shrink"
      >
        <v-icon
          icon="mdi-play-circle"
          size="large"
        />
      </a>
    </div>

    <!-- サイドバー本体部 -->
    <v-navigation-drawer
      class="p-drawer__main"
      :width="sidebarWidth"
      location="right"
      permanent
    >
      <div v-show="!isSidebarWidthMin">
        <v-tabs
          v-model="selectedTab"
          :height="40"
          bg-color="grey-lighten-3"
        >
          <v-tab
            class="ma-0"
            :height="40"
            value="userInfo"
          >
            <v-icon class="mr-1">
              mdi-magnify
            </v-icon>
            <span class="font-weight-bold">ユーザー情報</span>
          </v-tab>
          <v-tab
            class="ma-0 ml-4"
            :height="40"
            value="journal"
          >
            <v-icon class="mr-1">
              mdi-book-open-page-variant
            </v-icon>
            <span class="font-weight-bold">ジャーナル検索</span>
          </v-tab>
        </v-tabs>

        <div class="p-drawer__tab-body">
          <reservation-user-info-tab
            v-show="selectedTab === 'userInfo'"
            ref="userInfoTabRef"
            :reservation="reservation || {}"
          />
          <journal-tab
            v-show="selectedTab === 'journal'"
            ref="journalTabRef"
            class="p-drawer__journal-tab"
            type="reservation"
          /> <!-- タブ「ジャーナル検索」 -->
        </div>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";

// ジャーナル検索
import JournalTab from "@/vue/components/shared/sidebar/Journal/journalTab.vue";
import ReservationUserInfoTab from "./ReservationUserInfoTab.vue";

// サイドバーのデフォルトの展開幅 最小=1, 最大=3
const SIDEBAR_DEFAULT_RANK = 2;

const store = useStore();
const route = useRoute();

// 子コンポーネントの参照
const journalTabRef = ref(null);
const userInfoTabRef = ref(null);
const selectedTab = ref("userInfo");

// const sidebarTabId = computed(() => store.getters.sidebarTabId);
const sidebarWidthRank = computed(() => store.getters.sidebarWidthRank);
const sidebarWidth = computed(() => store.getters.sidebarWidth);
const isSidebarWidthMin = computed(() => store.getters.isSidebarWidthMin);
const isSidebarWidthMax = computed(() => store.getters.isSidebarWidthMax);
const reservation = computed(() => store.getters.reservation);
const reservationSidebarWidthRankByUser = computed(() => store.getters.reservationSidebarWidthRankByUser);

// 現在表示中ユーザーのIDを返す。予約がまだ未取得の場合は null
const currentUserId = computed(() => reservation.value?.userId ?? null);

// サイドバー幅を変更し、ユーザーごとの設定をストアに保存する
const updateWidthRank = (newRank) => {
  store.dispatch("updateSidebarWidthRank", newRank);
  if (currentUserId.value != null) {
    store.dispatch("saveReservationSidebarWidthRankForUser", { userId: currentUserId.value, rank: newRank });
  }
};

const expand = () => {
  if (!isSidebarWidthMax.value) updateWidthRank(sidebarWidthRank.value + 1);
};

const shrink = () => {
  if (!isSidebarWidthMin.value) updateWidthRank(sidebarWidthRank.value - 1);
};

// 外部（Karte.vue）からタブを直接切り替えるための公開メソッド
const selectTab = (tabValue) => {
  selectedTab.value = tabValue;
  if (isSidebarWidthMin.value) expand();
};

// --- ▼ ReservationのFetchが完了/失敗したタイミングで、サイドバーの各タブの初期化をトリガーする ---

// 直前に初期化した予約ID。同じ予約の再フェッチでは、サイドバー幅・タブ選択をリセットしないために使用する。
const initializedForReservationId = ref(null);

const normalizeReservationId = (value) => {
  const id = Number(value);
  return Number.isNaN(id) ? null : id;
};

const initializeTabs = () => {
  const targetId = normalizeReservationId(route.query.reservation_id);
  const isTargetReservation = targetId != null && !!reservation.value && reservation.value.id == targetId;

  // 別予約への遷移時のみサイドバー幅とタブ選択をリセットする
  if (initializedForReservationId.value !== targetId) {
    initializedForReservationId.value = targetId;
    selectedTab.value = "userInfo";
    const userId = isTargetReservation ? reservation.value.userId : null;
    const savedRank = userId != null ? (reservationSidebarWidthRankByUser.value[userId] ?? SIDEBAR_DEFAULT_RANK) : SIDEBAR_DEFAULT_RANK;
    store.dispatch("updateSidebarWidthRank", savedRank);
  }

  if (isTargetReservation) {
    if (journalTabRef.value) journalTabRef.value.start();
    if (userInfoTabRef.value) userInfoTabRef.value.start();
  } else {
    if (journalTabRef.value) journalTabRef.value.clear();
    if (userInfoTabRef.value) userInfoTabRef.value.clear();
  }
};

// コンポーネントのマウント時と、reservationの変更時にサイドバーの各タブの初期化を実行
onMounted(() => {
  initializeTabs();
});
watch(
  () => reservation.value,
  () => {
    initializeTabs();
  }
);
// --- ▲ ReservationのFetchが完了/失敗したタイミングで、サイドバーの各タブの初期化をトリガーする ---

defineExpose({
  selectTab,
});
</script>

<style lang="scss">
html:has(.reservation-sidebar) {
  overflow: hidden !important; /* スクロールバーを非表示にする */
}
</style>

<style lang="scss" scoped>
.p-drawer {
  &__icons {
    position: absolute;
    z-index: 2000;
    /* v-navigation-drawerになぜか動的にz-indexが指定される */
    margin-left: -12px;
    display: flex;
    flex-direction: column;
    margin-top: 50px;

    &__expand {
      transform: rotate(180deg);
      height: 25px;

      &.is-disable {
        opacity: 0;
      }
    }

    &__shrink {
      height: 25px;
      margin-top: 8px;

      &.is-disable {
        opacity: 0;
      }
    }
  }

  &__main {
    border-left: 1px solid #C9C5C5;
    height: 100% !important;
    overflow: auto;
  }

  &__minimized {
    display: block;
    height: 100%;
    width: 100%;
    text-align: center;
  }

  &__minimized-icon {
    padding-top: 80px;
    font-size: 20px;
    margin-top: 10px;
  }

  &__minimized-text {
    writing-mode: vertical-rl;
    padding-top: 15px;
    letter-spacing: 0.5em;
    font-size: 14px;
    width: 18px;
  }

  &__header {
    height: 40px;
    background-color: #eee;
    border-bottom: 1px solid #ccc;
  }

  &__tab-body {
    min-height: calc(100vh - 152px - 64px);
    background-color: #f7f7f7;
  }

  &__journal-tab {
    font-size: 14px;
  }

  :deep(.v-navigation-drawer) {
    /* absoluteがつくとflexでうまく配置されない */
    position: initial !important;
  }
}
</style>
